import { useEffect, useState } from 'react';

import { useStackListing } from '@/lib/api/stackHooks';
import { stackCartChanges } from './stack';
import { cart, type CartItem } from './stores/cart';

/** Stacks persisted in the cart are checked against a fresh Shop listing each
 *  time the cart opens, before the patient can confirm: a stack the Shop no
 *  longer sells them leaves the cart and one whose price changed is
 *  repriced, both with a notice, so a confirm never charges a different
 *  amount than the cart shows. Pay-link lines keep the price they were sold
 *  at and are never checked. */
export function useStackCartCheck(open: boolean, items: CartItem[], locked: boolean) {
  const active = open && items.some((item) => item.stack_id && !item.stack_recovery);
  const listing = useStackListing(active);
  // When this opening's fresh read started; 0 while there's nothing to check.
  const [since, setSince] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const { refetch } = listing;

  // Enabling the (always stale) listing fetches it once; only data read
  // after that counts.
  useEffect(() => {
    setSince(active ? Date.now() : 0);
  }, [active]);
  useEffect(() => {
    if (!open) setNotice(null);
  }, [open]);

  const fresh =
    since > 0 &&
    listing.isSuccess &&
    !listing.isFetching &&
    listing.dataUpdatedAt >= since;
  const changes = fresh && listing.data ? stackCartChanges(items, listing.data) : [];
  const pending = changes.length > 0;

  useEffect(() => {
    if (!pending || locked || !listing.data) return;
    const current = stackCartChanges(cart.getSnapshot().items, listing.data);
    for (const change of current) {
      if (change.lines) cart.addStack(change.lines);
      else cart.removeStack(change.stackId);
    }
    const messages = current.map((change) => change.message).filter(Boolean);
    if (messages.length) setNotice(messages.join(' '));
  }, [pending, locked, listing.data]);

  return {
    /** Nothing to check, or every stack matches a fresh listing. */
    ready: !active || (fresh && !pending),
    checking: active && !fresh && !(listing.isError && !listing.isFetching),
    failed: active && listing.isError && !listing.isFetching,
    retry: () => {
      setSince(Date.now());
      void refetch();
    },
    notice,
    clearNotice: () => setNotice(null),
  };
}
